import { useCallback, useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

import MonthDropdown from "../../../../components/inputs/MonthDropdown";
import YearDropdown from "../../../../components/inputs/YearDropdown";
import { DropdownOption } from "../../../../components/inputs/Dropdown";

import {
  generateCreditNoteByStationId,
  generateCreditNotes,
  getCustomerCreditNotes,
} from "../../../../services/clearing/credit-notes";
import { finalizeDocument } from "../../../../services/clearing/bills";
import CustomerOwnedStationDropdown from "../../../../components/inputs/CustomerOwnedStationsDropdown";

interface CreditNotesGenerationToolbarProps {
  customerId: string;
  customerEmail?: string;
}

const today = new Date();

const CreditNotesGenerationToolbar = ({
  customerId,
  customerEmail,
}: CreditNotesGenerationToolbarProps) => {
  const [selectedMonth, setSelectedMonth] = useState<number | null>(
    today.getMonth()
  );

  const [selectedYear, setSelectedYear] = useState<number | null>(
    today.getFullYear()
  );

  const [selectedStation, setSelectedStation] = useState({
    name: "all",
    code: null,
  });

  const [currentCreditNotes, setCurrentCreditNotes] = useState([]);
  const [creditNotesError, setCreditNotesError] = useState<string | null>(null);
  const [creditNotesLoading, setCreditNotesLoading] = useState(false);

  const toast = useRef<Toast>(null);

  const fetchCurrentCreditNotes = useCallback(async () => {
    if (!customerId) return;
    setCreditNotesLoading(true);
    setCreditNotesError(null);
    try {
      const data = await getCustomerCreditNotes({ customerId });
      setCurrentCreditNotes(data || []);
    } catch (e: any) {
      const message =
        e?.response?.data?.message || e?.response?.data || e?.message || "Failed to fetch current credit notes";
      setCreditNotesError(String(message));
      setCurrentCreditNotes([]);
    } finally {
      setCreditNotesLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchCurrentCreditNotes();
  }, [fetchCurrentCreditNotes]);

  const onFinalizeAndSend = useCallback((documentId: number) => {
    confirmDialog({
      header: "Finalize and Send",
      message: (
        <div>
          <p>You are about to finalize and send document <strong>{documentId}</strong> to:</p>
          <p><strong>{customerEmail || "unknown email"}</strong></p>
          <p style={{ color: "red", fontWeight: "bold" }}>THIS ACTION CAN NOT BE REVERSED</p>
        </div>
      ),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Send and Finalize",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await finalizeDocument(documentId);
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: `Document ${documentId} finalized and sent.`,
            life: 3000,
          });
          fetchCurrentCreditNotes();
        } catch (e: any) {
          const message =
            e?.response?.data?.message || e?.response?.data || e?.message || "Failed to finalize document";
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: String(message),
            life: 5000,
          });
        }
      },
    });
  }, [customerEmail, fetchCurrentCreditNotes]);

  const onGenerateCreditNotes = useCallback(
    async ({ inDetail = false, stationId = null }) => {
      const success =
        stationId !== null
          ? await generateCreditNoteByStationId({
              stationId,
              month: selectedMonth,
              year: selectedYear,
            })
          : await generateCreditNotes({
              customerId,
              month: selectedMonth,
              year: selectedYear,
              inDetail,
            });

      if (success) {
        toast?.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Successfully generated credit note!",
          life: 3000,
        });
      } else {
        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to generate credit note!",
          life: 3000,
        });
      }
    },
    [selectedMonth, selectedYear, customerId]
  );

  return (
    <section className="py-4">
      <ConfirmDialog />
      <Toast ref={toast} />
      <Card title="Generate Credit Notes" id="credit-notes-generation">
        <form className="grid align-items-center">
          <div className="col-12 md:col-3">
            <MonthDropdown
              monthIndex={selectedMonth}
              onChange={(opt) => setSelectedMonth(Number(opt.code))}
              placeholder="Select Month"
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-3">
            <YearDropdown
              year={selectedYear}
              onChange={(opt: DropdownOption | null) =>
                setSelectedYear(Number(opt.code))
              }
              placeholder="Select Year"
              className="w-full"
              yearsOffset={2}
            />
          </div>

          <div className="col-12 md:col-3">
            <CustomerOwnedStationDropdown
              customerId={customerId}
              value={selectedStation as DropdownOption}
              onChange={setSelectedStation}
            />
          </div>

          <div className="col-12 md:col-3 flex flex-column gap-2 justify-content-center">
            <Button
              label="Generate Credit Notes"
              onClick={(e) => {
                e.preventDefault();

                const stationId = selectedStation?.code || null;
                if (stationId !== null) {
                  console.log(
                    "Generating credit note for station",
                    selectedStation
                  );

                  onGenerateCreditNotes({ stationId });
                  return;
                }

                onGenerateCreditNotes({ inDetail: false });
              }}
              className="w-full"
            />
            <Button
              label="Generate Credit Notes In Detail"
              onClick={(e) => {
                e.preventDefault();
                onGenerateCreditNotes({ inDetail: true });
              }}
              className="w-full"
            />
          </div>
        </form>
      </Card>

      <Card title="Current Credit Notes" className="mt-3">
        <div className="flex justify-content-end mb-3">
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            onClick={() => fetchCurrentCreditNotes()}
            className="p-button-outlined"
            loading={creditNotesLoading}
          />
        </div>
        {creditNotesLoading && (
          <div className="flex justify-content-center my-3">
            <ProgressSpinner />
          </div>
        )}
        {creditNotesError && (
          <Message severity="error" text={creditNotesError} className="w-full mb-3" />
        )}
        {!creditNotesLoading && !creditNotesError && (
          <DataTable value={currentCreditNotes} sortField="date" sortOrder={-1} emptyMessage="No credit notes found.">
            <Column field="date" header="Date" sortable />
            <Column field="documentId" header="Document ID" />
            <Column field="dueInDays" header="Due in Days" />
            <Column field="vatOption" header="VAT Option" />
            <Column
              field="isDraft"
              header="Draft"
              body={(row) => (row.isDraft ? "Yes" : "No")}
            />
            <Column
              field="easybillUrl"
              header="Easybill"
              body={(row) =>
                row.easybillUrl ? (
                  <Button
                    label="Show in Easybill"
                    icon="pi pi-external-link"
                    className="p-button-sm p-button-outlined"
                    onClick={() => window.open(row.easybillUrl, "_blank", "noopener,noreferrer")}
                  />
                ) : (
                  "-"
                )
              }
            />
            <Column
              header="Actions"
              body={(row) =>
                row.isDraft ? (
                  <Button
                    label="Finalize & Send"
                    icon="pi pi-send"
                    className="p-button-warning p-button-sm"
                    onClick={() => onFinalizeAndSend(row.documentId)}
                  />
                ) : null
              }
            />
          </DataTable>
        )}
      </Card>
    </section>
  );
};

export default CreditNotesGenerationToolbar;
